import { backendApi } from "@/services/backend/api";
import { AirHandlingUnitData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { RFC3339timestamp, UUID } from "@bitory-gmbh/nidavellir";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const htzAhuDataApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    htzAirHandlingUnitDataPut: build.mutation<AirHandlingUnitData, PutRequest>({
      query: (request) => ({
        url: `/htz/air-handling-unit-data`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzAirHandlingUnitData", id: request.id },
        { type: "HtzAirHandlingUnitData", id: "LIST" },
      ],
    }),
    htzAirHandlingUnitDataShow: build.query<AirHandlingUnitData, ShowRequest>({
      query: (request) => ({
        url: `/htz/air-handling-unit-data`,
        method: "GET",
        params: request,
      }),
      providesTags: providesEntity("HtzAirHandlingUnitData"),
    }),
    htzAirHandlingUnitDataList: build.query<
      Lst<AirHandlingUnitData>,
      ListRequest
    >({
      query: (request) => ({
        url: `/htz/air-handling-unit-data/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        providesList(result?.items, "HtzAirHandlingUnitData"),
    }),
    htzAirHandlingUnitDataDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/htz/air-handling-unit-data`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["HtzAirHandlingUnitData"],
    }),
  }),
});

export const {
  useHtzAirHandlingUnitDataPutMutation,
  useHtzAirHandlingUnitDataShowQuery,
  useHtzAirHandlingUnitDataListQuery,
  useLazyHtzAirHandlingUnitDataListQuery,
  useHtzAirHandlingUnitDataDeleteMutation,
} = htzAhuDataApi;

export type PutRequest = AirHandlingUnitData;

export interface ShowRequest {
  id: string;
}

export interface ListRequest {
  id?: Filter<UUID>;
  airHandlingUnit?: Filter<UUID>;
  company?: Filter<UUID>;
  owningWorkOrder?: Filter<UUID>;
  owningReport?: Filter<UUID>;
  customer?: Filter<UUID>;
  serviceObject?: Filter<UUID>;
  label?: Filter<string>;
  location?: Filter<string>;
  constructionYear?: Filter<string>;
  volumeFlowRate?: Filter<string>;
  createdAt?: Filter<RFC3339timestamp>;
  updatedAt?: Filter<RFC3339timestamp>;
  orderBy?: OrdrBy<AhuDataOrderBy>[];
  limit?: number;
  offset?: number;
}

export type AhuDataOrderBy =
  | "designation"
  | "label"
  | "location"
  | "constructionYear"
  | "volumeFlowRate"
  | "createdAt"
  | "updatedAt";

export interface DeleteRequest {
  id: string;
}

// Optimistically patch the cache entry. Dispatch this action
// for immediate UI updates before the debounced mutations fire.
export const updateAhuDataCache = updateCache(
  htzAhuDataApi,
  "htzAirHandlingUnitDataShow",
);
